import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';

import { useLeaderboard, useLeaderboardMultiple } from '@/lib/hooks/useLms';
import { useCurrentBatch } from '@/lib/hooks/useCurrentBatch';
import { LeaderboardList } from '@/features/Leaderboard/LeaderboardList';
import { MyStandingCard } from '@/features/Leaderboard/MyStandingCard';
import { ScoreBreakdown } from '@/features/Leaderboard/ScoreBreakdown';
import { BiggestLeverCard } from '@/features/Leaderboard/BiggestLeverCard';
import type { BatchLeaderboard } from '@/lib/api/lms';

export const Route = createFileRoute('/_student/leaderboard/')({ component: LeaderboardPage });

function LeaderboardSkeleton() {
  return (
    <div className="grid animate-pulse gap-11 lg:grid-cols-[1fr_320px]">
      <div className="space-y-4">
        <div className="h-64 rounded-xl bg-muted" />
        <div className="h-52 rounded-xl bg-muted" />
      </div>
      <div className="space-y-4">
        <div className="h-44 rounded-xl bg-muted" />
        <div className="h-40 rounded-xl bg-muted" />
      </div>
    </div>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-10 text-center text-muted-foreground">
      {children}
    </div>
  );
}

// Exported for testing without a router.
export function LeaderboardSection({ data, batchId }: { data: BatchLeaderboard; batchId: string }) {
  const me = data.neighbourhood.find((e) => e.is_caller) ?? data.top.find((e) => e.is_caller);
  const solo = data.batch.total_students === 1;

  if (data.batch.total_students === 0) {
    return <EmptyState>No students enrolled in {data.batch.name} yet.</EmptyState>;
  }

  return (
    // Mobile order matters: the standing card must come first, because on a
    // phone the first viewport should answer "how am I doing", not "who is
    // winning". lg:order-* restores the two-column reading order on desktop.
    <div className="grid gap-11 lg:grid-cols-[1fr_320px]">
      <div className="space-y-8 lg:order-1">
        {solo ? (
          <EmptyState>You&apos;re the only student enrolled in {data.batch.name} right now.</EmptyState>
        ) : (
          <>
            <LeaderboardList entries={data.top} title={`Top of ${data.scope_label}`} aside="Readiness" />
            <LeaderboardList
              entries={data.neighbourhood}
              title="Around you"
              aside={`Ranks 11–${data.batch.total_students} are private`}
            />
          </>
        )}
      </div>

      <div className="max-lg:order-first lg:order-2 [&>section+section]:mt-8 [&>section+section]:border-t [&>section+section]:border-border/60 [&>section+section]:pt-8">
        <MyStandingCard data={data} />
        {/* Breakdown and lever read the v2 scored breakdown from /lms/home
            (the leaderboard payload still carries only the legacy flat
            components), so they no longer depend on `me.components`. */}
        {me && (
          <>
            <ScoreBreakdown batchId={batchId} />
            <BiggestLeverCard batchId={batchId} />
          </>
        )}
      </div>
    </div>
  );
}

// Exported for testing without a router.
export function ScopedLeaderboard({ batchId }: { batchId: string }) {
  const [scope, setScope] = useState<'batch' | 'course'>('batch');
  // Fetch both scopes concurrently so the course tab can always be labelled
  // from its OWN scoped result, rather than from whichever scope happens to
  // be selected — otherwise the course tab shows the batch's name until it's
  // clicked once. Both are 15-minute server-cached, so two requests per page
  // load is cheap; only WHICH result is rendered changes with `scope`.
  const batchResult = useLeaderboard(batchId, 'batch');
  const courseResult = useLeaderboard(batchId, 'course');
  const active = scope === 'batch' ? batchResult : courseResult;

  return (
    <>
      <div className="inline-flex gap-1 rounded-lg border border-border bg-muted p-1">
        {(['batch', 'course'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setScope(s)}
            aria-pressed={scope === s}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
              scope === s ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {s === 'batch' ? 'My batch' : courseResult.data?.scope_label ?? 'My course'}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {active.isLoading ? (
          <LeaderboardSkeleton />
        ) : !active.data ? (
          <EmptyState>No leaderboard data available yet.</EmptyState>
        ) : (
          <LeaderboardSection data={active.data} batchId={batchId} />
        )}
      </div>
    </>
  );
}

function LeaderboardPage() {
  const { batchId, isAll, batches, isLoading: batchesLoading } = useCurrentBatch();
  const multiResults = useLeaderboardMultiple(isAll ? batches.map((b) => b.id) : []);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-extrabold text-foreground">Leaderboard</h1>
        <p className="text-sm text-muted-foreground">
          Ranked by readiness — the same score your placement cell sees.
        </p>
      </div>

      {batchesLoading ? (
        <LeaderboardSkeleton />
      ) : isAll ? (
        // Ranks are inherently per-cohort — there is no meaningful merged
        // ranking across batches, so render one section each.
        multiResults.every((r) => !r.data) ? (
          <EmptyState>No leaderboard data available for your batches yet.</EmptyState>
        ) : (
          multiResults
            .map((r) => r.data)
            .filter((d): d is BatchLeaderboard => !!d)
            .map((d) => (
              <div key={d.batch.id} className="mt-6">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                  {d.batch.name} · {d.batch.total_students} students
                </p>
                <LeaderboardSection data={d} batchId={d.batch.id} />
              </div>
            ))
        )
      ) : batchId ? (
        <ScopedLeaderboard batchId={batchId} />
      ) : (
        <EmptyState>No students enrolled in this batch yet.</EmptyState>
      )}
    </div>
  );
}
